import axios from "axios";
import { notify } from "@kyvg/vue3-notification";
import { getLocalProhorReply } from "@/store/prohor/prohorLocalFallbacks";
import { findLine1Reply } from "@/store/prohor/prohorLine1";
import { findHrGuideReply } from "@/store/prohor/prohorHrGuide";
import { buildInventoryReply, isInventoryQuestion } from "@/store/prohor/prohorInventory";
import {
    handleCoursePathQuestion,
    isCoursePathQuestion,
} from "@/store/prohor/prohorCoursePath";
import { buildPageContextReply, detectPageContextMode } from "@/store/prohor/prohorPageContext";
import { getPageHint, hasPageHint } from "@/store/prohor/prohorPageHints";
import { formatProhorReply } from "@/store/prohor/prohorReplyFormat";
import { filterProhorActionsByAccess } from "@/store/prohor/prohorRouteAccess";

const ONBOARDING_STORAGE_KEY = "prohor_onboarding_seen";
const HISTORY_STORAGE_KEY = "prohor_chat_history";
const HISTORY_LIMIT = 100;

const createMessage = ({
    role,
    text,
    status = "sent",
    suggestions = [],
    actions = [],
}) => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role,
    text,
    status,
    suggestions,
    actions,
    createdAt: new Date().toISOString(),
});

const loadHistory = () => {
    try {
        const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
};

const persistHistory = (messages) => {
    try {
        const limited = messages.slice(-HISTORY_LIMIT);
        localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(limited));
    } catch (e) {
        // ignore quota errors
    }
};

const QUICK_REPLIES = {
    "Что умеет помощник": {
        userText: "Что умеет помощник",
        assistantText:
            "Я помогаю двумя вещами:\n" +
            "1) Подсказываю по навигации в личном кабинете с учётом вашей роли — " +
            "например, как купить тесты, пригласить сотрудников или посмотреть отчёт.\n" +
            "2) Отвечаю на вопросы из Базы знаний по эмоциональному интеллекту.\n\n" +
            "Задайте вопрос в поле ниже — и я подберу ответ.",
    },
    "База знаний": {
        userText: "База знаний",
        assistantText:
            "База знаний — библиотека материалов по эмоциональному интеллекту: " +
            "методички, статьи, исследования и практические упражнения.\n\n" +
            "Можете задать теоретический вопрос или попросить подобрать материалы под задачу — " +
            "я найду ответ в загруженных источниках.",
    },
};

const buildActionsFromReply = (reply) => {
    if (!reply) return [];
    if (Array.isArray(reply.actions) && reply.actions.length) {
        return reply.actions;
    }
    if (reply.action) return [reply.action];
    return [];
};

const replyWithAccessFilter = (reply, role, hasSubscription) => {
    if (!reply) return null;
    const formatted = formatProhorReply(reply);
    const actions = filterProhorActionsByAccess({
        actions: formatted.actions,
        role,
        hasSubscription,
    });
    return { text: formatted.text, actions };
};

export default {
    namespaced: true,

    state: () => ({
        chatOpen: false,
        loading: false,
        onboardingSeen: localStorage.getItem(ONBOARDING_STORAGE_KEY) === "1",
        messages: loadHistory(),
    }),

    mutations: {
        setChatOpen(state, value) {
            state.chatOpen = value;
        },
        setLoading(state, value) {
            state.loading = value;
        },
        setOnboardingSeen(state) {
            state.onboardingSeen = true;
            try {
                localStorage.setItem(ONBOARDING_STORAGE_KEY, "1");
            } catch (e) {
                // ignore
            }
        },
        addMessage(state, message) {
            state.messages.push(message);
            persistHistory(state.messages);
        },
        updateMessage(state, { id, patch }) {
            const idx = state.messages.findIndex((m) => m.id === id);
            if (idx === -1) return;
            state.messages[idx] = { ...state.messages[idx], ...patch };
            persistHistory(state.messages);
        },
        clearMessages(state) {
            state.messages = [];
            persistHistory(state.messages);
        },
    },

    actions: {
        openChat({ commit }) {
            commit("setChatOpen", true);
            commit("setOnboardingSeen");
        },
        closeChat({ commit }) {
            commit("setChatOpen", false);
        },
        toggleChat({ commit, state }) {
            commit("setChatOpen", !state.chatOpen);
            if (!state.onboardingSeen) {
                commit("setOnboardingSeen");
            }
        },
        dismissOnboarding({ commit }) {
            commit("setOnboardingSeen");
        },
        resetChat({ commit }) {
            commit("clearMessages");
        },
        sendQuickReply({ commit }, actionKey) {
            const reply = QUICK_REPLIES[actionKey];
            if (!reply) return;

            commit(
                "addMessage",
                createMessage({ role: "user", text: reply.userText })
            );
            commit(
                "addMessage",
                createMessage({ role: "assistant", text: reply.assistantText })
            );
        },

        sendPageHelpPrompt({ commit }, { path }) {
            if (!path || !hasPageHint(path)) return;

            commit(
                "addMessage",
                createMessage({
                    role: "assistant",
                    text:
                        "Могу подсказать, что можно сделать на этой странице. " +
                        "Нажмите вопрос ниже или напишите свой.",
                    suggestions: ["Что я могу сделать на этой странице?"],
                })
            );
        },

        sendPageHelp({ commit }, { path, role, hasSubscription }) {
            const hint = getPageHint(path, role);
            if (!hint) return;

            commit(
                "addMessage",
                createMessage({
                    role: "user",
                    text: "Помоги на этой странице",
                })
            );

            const filtered = replyWithAccessFilter(
                {
                    text: hint.whatCanDo || hint.text,
                    actions: hint.actions,
                },
                role,
                hasSubscription
            );

            if (!filtered) return;

            commit(
                "addMessage",
                createMessage({
                    role: "assistant",
                    text: filtered.text,
                    actions: filtered.actions,
                })
            );
        },

        async askQuestion({ commit, dispatch }, payload) {
            const question =
                typeof payload === "string" ? payload : payload?.question;
            const role = typeof payload === "object" ? payload?.role : null;
            const path = typeof payload === "object" ? payload?.path : null;
            const hasSubscription =
                typeof payload === "object" ? !!payload?.hasSubscription : false;

            const trimmed = (question ?? "").trim();
            if (trimmed.length < 2 || trimmed.length > 1000) {
                notify({
                    title: "Введите вопрос от 2 до 1000 символов",
                    type: "warn",
                });
                return;
            }

            commit(
                "addMessage",
                createMessage({ role: "user", text: trimmed })
            );

            const fallback = getLocalProhorReply(trimmed);
            if (fallback) {
                commit(
                    "addMessage",
                    createMessage({ role: "assistant", text: fallback.text })
                );
                return;
            }

            if (detectPageContextMode(trimmed)) {
                const pageReply = buildPageContextReply(trimmed, path, role);
                const filtered = replyWithAccessFilter(
                    pageReply,
                    role,
                    hasSubscription
                );
                if (filtered) {
                    commit(
                        "addMessage",
                        createMessage({
                            role: "assistant",
                            text: filtered.text,
                            actions: filtered.actions,
                        })
                    );
                    return;
                }
            }

            if (isCoursePathQuestion(trimmed)) {
                commit("setLoading", true);
                try {
                    const courseReply = await handleCoursePathQuestion(trimmed);
                    if (courseReply) {
                        const filtered = replyWithAccessFilter(
                            courseReply,
                            role,
                            hasSubscription
                        );
                        commit(
                            "addMessage",
                            createMessage({
                                role: "assistant",
                                text: filtered?.text || courseReply.text,
                                actions: filtered?.actions || courseReply.actions,
                                suggestions: courseReply.suggestions || [],
                            })
                        );
                        return;
                    }
                } finally {
                    commit("setLoading", false);
                }
            }

            if (isInventoryQuestion(trimmed)) {
                const inventory = await buildInventoryReply(role);
                const filtered = replyWithAccessFilter(
                    inventory,
                    role,
                    hasSubscription
                );
                if (filtered) {
                    commit(
                        "addMessage",
                        createMessage({
                            role: "assistant",
                            text: filtered.text,
                            actions: filtered.actions,
                        })
                    );
                    return;
                }
            }

            const hrGuide = findHrGuideReply(trimmed, role);
            if (hrGuide) {
                const filtered = replyWithAccessFilter(
                    hrGuide,
                    role,
                    hasSubscription
                );
                if (filtered) {
                    commit(
                        "addMessage",
                        createMessage({
                            role: "assistant",
                            text: filtered.text,
                            actions: filtered.actions,
                        })
                    );
                    return;
                }
            }

            const line1 = findLine1Reply(trimmed, role);
            if (line1) {
                const filtered = replyWithAccessFilter(
                    line1,
                    role,
                    hasSubscription
                );
                if (filtered) {
                    commit(
                        "addMessage",
                        createMessage({
                            role: "assistant",
                            text: filtered.text,
                            actions: filtered.actions,
                        })
                    );
                    return;
                }
            }

            await dispatch("requestKnowledge", trimmed);
        },

        async requestKnowledge({ commit }, question) {
            const pendingMessage = createMessage({
                role: "assistant",
                text: "",
                status: "pending",
            });
            commit("addMessage", pendingMessage);
            commit("setLoading", true);

            try {
                const response = await axios.post(
                    "/ai/knowledge",
                    { question },
                    { notifyShow: false }
                );

                if (!response) {
                    throw new Error("Empty response");
                }

                const data = response.data;
                const answer = data?.response?.text;

                commit("updateMessage", {
                    id: pendingMessage.id,
                    patch: {
                        text:
                            answer ||
                            "К сожалению, ответ не получен. Попробуйте переформулировать вопрос.",
                        status: "sent",
                    },
                });
            } catch (error) {
                commit("updateMessage", {
                    id: pendingMessage.id,
                    patch: {
                        text:
                            "Не удалось получить ответ. Пожалуйста, попробуйте ещё раз чуть позже.",
                        status: "error",
                    },
                });
                notify({
                    title: "Ошибка при обращении к ПРОХОРУ",
                    type: "error",
                });
            } finally {
                commit("setLoading", false);
            }
        },

        askKnowledge({ dispatch }, payload) {
            return dispatch("askQuestion", payload);
        },
    },
};
